import type { ScreeningOutcome } from '@kiku/config/constants';
import { CircleHelp, ClipboardX } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '~/components/ui/badge';
import { Progress } from '~/components/ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '~/components/ui/tooltip';

interface ScoreOverviewCardProps {
  overallScore: number;
  outcome: ScreeningOutcome;
  aiSummary: string;
  hasCopyPasteDetected?: boolean;
}

function getOutcomeVariant(outcome: ScreeningOutcome): 'default' | 'destructive' {
  return outcome === 'pass' ? 'default' : 'destructive';
}

export function ScoreOverviewCard({
  overallScore,
  outcome,
  aiSummary,
  hasCopyPasteDetected
}: ScoreOverviewCardProps): React.ReactNode {
  const { t } = useTranslation();

  return (
    <div className="rounded-lg border border-border bg-card p-5 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-3">
          <h2 className="text-base font-semibold">{t('scoring.overallScore')}</h2>
          <div className="flex items-center gap-2">
            <span className="text-3xl font-semibold">{overallScore.toFixed(1)}</span>
            <span className="text-sm text-muted-foreground">/ 5</span>
          </div>
          <Progress value={(overallScore / 5) * 100} className="h-2 w-48" />
        </div>
        <Badge variant={getOutcomeVariant(outcome)} className="text-sm">
          {t(`scoring.outcomes.${outcome}` as 'scoring.outcomes.pass')}
        </Badge>
      </div>
      <div className="mt-4 border-t border-border pt-4">
        <h3 className="mb-1 text-sm font-medium text-muted-foreground">{t('scoring.summary')}</h3>
        <p className="text-sm">{aiSummary}</p>
      </div>
      {hasCopyPasteDetected && (
        <div className="mt-4 flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-700">
          <ClipboardX className="size-4 shrink-0" />
          <span className="flex-1">{t('scoring.copyPasteWarning')}</span>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger className="flex items-center text-amber-500 hover:text-amber-700">
                <CircleHelp className="size-4" />
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-64">
                {t('scoring.copyPasteWarningTooltip')}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      )}
    </div>
  );
}
